import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { Database } from '@/_constants/database.types';

type PracticeSet = Database['public']['Tables']['practice_attempts']['Row'];
type PracticeAnswer = Database['public']['Tables']['practice_answers']['Row'];
type Question = Database['public']['Tables']['questions']['Row'];

export interface PracticeAnswerWithQuestion extends PracticeAnswer {
  questions: Pick<Question, 'id' | 'content' | 'marks' | 'category' | 'category_code' | 'image_page_numbers' | 'mark_scheme_content'>;
}

export interface PracticeSetWithDetails extends PracticeSet {
  practice_answers: PracticeAnswerWithQuestion[];
}

const useAllUserPracticeSets = (authId: string | undefined) => {
  return useQuery({
    queryKey: ['practice_attempts', authId],
    queryFn: async (): Promise<PracticeSetWithDetails[]> => {
      if (!authId) return [];

      const { data, error } = await supabaseClient
        .from('practice_attempts')
        .select(
          `
          *,
          practice_answers (
            *,
            questions (
              id,
              content,
              marks,
              category,
              category_code,
              image_page_numbers,
              mark_scheme_content
            )
          )
        `
        )
        .eq('auth_id', authId)
        .eq('status', 'graded')
        .order('created_at', { ascending: false })
        .order('question_order', { referencedTable: 'practice_answers', ascending: true });

      if (error) throw error;

      return data as PracticeSetWithDetails[];
    },
    enabled: !!authId,
  });
};

export default useAllUserPracticeSets;
